import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/AuthPage.css";
import RegisterForm from "../components/RegisterForm";
import Modal from "../components/Modal"; 
import { 
  Mail, 
  Lock, 
  Eye, 
  EyeOff
} from "lucide-react";

// ================= KOMPONEN SUCCESS OVERLAY =================
const SuccessOverlay = ({ userName, message }) => (
  <div className="premium-overlay-backdrop">
    <div className="premium-success-card">
      <div className="success-checkmark">
        <div className="check-icon">
          <span className="icon-line line-tip"></span>
          <span className="icon-line line-long"></span>
          <div className="icon-circle"></div>
          <div className="icon-fix"></div>
        </div>
      </div>
      <h2 className="premium-title">Login Berhasil!</h2>
      <p className="premium-subtitle">Selamat Datang, <b>{userName}</b></p>
      <div className="premium-loader-bar"></div>
      <p className="premium-redirect-text">{message}</p>
    </div>
  </div>
);

// ================= MAIN COMPONENT =================
const AuthPage = ({ onLogin }) => {
  // --- States ---
  const [isRegister, setIsRegister] = useState(false);
  const [isForgotPass, setIsForgotPass] = useState(false); // State untuk Lupa Password
  
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [forgotForm, setForgotForm] = useState({ email: "", newPassword: "", confirmPassword: "" }); // Form Lupa Password
  
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [successData, setSuccessData] = useState(null); 

  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_BASE_URL || "";

  // State Modal diperbarui untuk mendukung konfirmasi (ya/tidak)
  const [modal, setModal] = useState({
    isOpen: false,
    type: "info",
    message: "",
    onConfirm: null,
    confirmOnly: true, 
  });

  // --- Effects ---
  useEffect(() => {
    const savedEmail = localStorage.getItem("email");
    const savedPassword = localStorage.getItem("password");
    if (savedEmail && savedPassword) {
      setForm((f) => ({ ...f, email: savedEmail, password: savedPassword }));
      setRememberMe(true);
    }
  }, []);

  // --- Handlers ---
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleForgotChange = (e) => setForgotForm({ ...forgotForm, [e.target.name]: e.target.value });
  const handleRememberMeChange = (e) => setRememberMe(e.target.checked);
  const togglePassword = () => setShowPassword(!showPassword);

  const showModal = (type, message, onConfirm = null, confirmOnly = true) => {
    setModal({ isOpen: true, type, message, onConfirm, confirmOnly });
  };
  const closeModal = () => setModal((prev) => ({ ...prev, isOpen: false }));

  // --- API Calls ---

  // 1. Register
  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: form.name, email: form.email, password: form.password }),
      });
      const data = await res.json();

      if (data.result === "success") {
        const userData = {
          uid: data.uid || "", name: form.name, email: form.email,
          photo: data.photo || "", password: form.password, isAdmin: false,
        };
        localStorage.setItem("userData", JSON.stringify(userData));
        onLogin(userData);
        
        setLoading(false); 
        setSuccessData({ name: form.name, message: "Menyiapkan akun Anda..." });
        setTimeout(() => navigate("/homepage"), 1500);
      } else {
        setLoading(false);
        // FITUR EMAIL SAMA: Pesan error dari server ("Email sudah terdaftar!") akan masuk ke sini
        showModal("error", data.message || "Registrasi gagal", null, true);
      }
    } catch (err) {
      setLoading(false);
      showModal("error", "Koneksi server bermasalah.", null, true);
    }
  };

  // 2. Login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const res = await fetch(`${API}/api/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email, password: form.password }),
      });
      const data = await res.json();

      if (data.result === "success") {
        if (rememberMe) {
          localStorage.setItem("email", form.email);
          localStorage.setItem("password", form.password);
        } else {
          localStorage.removeItem("email");
          localStorage.removeItem("password");
        }

        const userName = data.name || form.email.split("@")[0];
        const userData = {
          uid: data.uid || "",
          name: userName,
          email: form.email,
          photo: data.photo || "",
          password: form.password,
          isAdmin: false,
        };

        localStorage.setItem("userData", JSON.stringify(userData));
        onLogin(userData);

        setLoading(false);
        setSuccessData({ name: userName, message: "Mengalihkan ke Dashboard..." });
        setTimeout(() => navigate("/homepage"), 1500);
      } else {
        setLoading(false);
        showModal("error", data.message || "Email atau password salah.", null, true);
      }
    } catch (err) {
      setLoading(false);
      showModal("error", "Terjadi kesalahan sistem. Coba lagi nanti.", null, true);
    }
  };

  // 3. Lupa Password
  const handleForgotSubmit = (e) => {
    e.preventDefault();
    
    // Validasi kecocokan password
    if (forgotForm.newPassword !== forgotForm.confirmPassword) {
      showModal("error", "Password baru dan konfirmasi tidak cocok!", null, true);
      return;
    }
    
    // Munculkan konfirmasi modal ganti password
    showModal(
      "info",
      "Apakah kamu yakin ingin mengganti password?",
      executeResetPassword,
      false // false agar memunculkan tombol konfirmasi Ya/Batal
    );
  };

  // Fungsi yang dijalankan jika user menekan "Ya" di Modal konfirmasi
  const executeResetPassword = async () => {
    setLoading(true);
    closeModal(); // Tutup modal konfirmasi
    
    try {
      const res = await fetch(`${API}/api/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotForm.email, newPassword: forgotForm.newPassword }),
      });
      const data = await res.json();
      
      if (data.result === "success") {
        setLoading(false);
        setForgotForm({ email: "", newPassword: "", confirmPassword: "" });
        setIsForgotPass(false); // Kembalikan ke halaman login
        showModal("success", "Password berhasil diubah! Silakan login dengan password baru.", null, true);
      } else {
        setLoading(false);
        showModal("error", data.message || "Gagal mereset password", null, true);
      }
    } catch (err) {
      setLoading(false);
      showModal("error", "Koneksi server bermasalah.", null, true);
    }
  };

  // --- Render ---
  return (
    <div className="authpage">
      <div className="bg-shape shape-1"></div>
      <div className="bg-shape shape-2"></div>

      {successData && (
        <SuccessOverlay userName={successData.name} message={successData.message} />
      )}

      <div className="container">
        <div className="form-container sign-in-container" style={{ width: "50%" }}>
          
          {/* LOGIKA RENDER FORM: Lupa Password -> Register -> Login */}
          {isForgotPass ? (
            <form onSubmit={handleForgotSubmit}>
              <h1>Lupa Password</h1>
              <p className="subtitle">Masukkan email terdaftar dan password barumu</p>

              <div className="input-group">
                <Mail className="input-icon" size={18} />
                <input
                  className="auth-input"
                  type="email"
                  name="email"
                  placeholder="Alamat Email"
                  value={forgotForm.email}
                  onChange={handleForgotChange}
                  required
                />
              </div>

              <div className="input-group">
                <Lock className="input-icon" size={18} />
                <input
                  className="auth-input"
                  type={showPassword ? "text" : "password"}
                  name="newPassword"
                  placeholder="Password Baru"
                  value={forgotForm.newPassword}
                  onChange={handleForgotChange}
                  required
                  minLength="6"
                />
                <div className="toggle-password" onClick={togglePassword}>
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </div>
              </div>

              <div className="input-group">
                <Lock className="input-icon" size={18} />
                <input
                  className="auth-input"
                  type={showPassword ? "text" : "password"}
                  name="confirmPassword"
                  placeholder="Konfirmasi Password Baru"
                  value={forgotForm.confirmPassword}
                  onChange={handleForgotChange}
                  required
                  minLength="6"
                />
              </div>

              <button className="btn-primary" type="submit" disabled={loading} style={{ marginTop: "15px" }}>
                {loading ? <span className="loader"></span> : "Ganti Password"}
              </button>

              <div className="divider"><span>Atau</span></div>

              <p className="switch-text">
                Ingat password kamu?{" "}
                <span className="text-link" onClick={() => setIsForgotPass(false)}>
                  Kembali ke Login
                </span>
              </p>
            </form>
          ) : isRegister ? (
            <RegisterForm
              form={form}
              setForm={setForm}
              handleChange={handleChange}
              loading={loading}
              setIsRegister={setIsRegister}
              handleRegister={handleRegister}
            />
          ) : (
            <form onSubmit={handleLogin}>
              <h1>Halo, Sahabat!</h1>
              <p className="subtitle">Masuk untuk mengakses layanan RSIA Annisa</p>
              
              <div className="input-group">
                <Mail className="input-icon" size={18} />
                <input
                  className="auth-input"
                  type="email"
                  name="email"
                  placeholder="Alamat Email"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="input-group">
                <Lock className="input-icon" size={18} />
                <input
                  className="auth-input"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Kata Sandi"
                  value={form.password}
                  onChange={handleChange}
                  required
                />
                <div className="toggle-password" onClick={togglePassword}>
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </div>
              </div>

              <div className="form-actions">
                <div className="remember">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={handleRememberMeChange}
                    id="rememberMe"
                  />
                  <label htmlFor="rememberMe">Ingat Saya</label>
                </div>
                <span 
                  className="text-link" 
                  onClick={() => setIsForgotPass(true)} 
                  style={{ fontSize: "0.9rem", cursor: "pointer" }}
                >
                  Lupa Password?
                </span>
              </div>

              <button className="btn-primary" type="submit" disabled={loading}>
                {loading ? <span className="loader"></span> : "Masuk"}
              </button>

              <div className="divider"><span>Atau</span></div>

              <p className="switch-text">
                Belum punya akun?{" "}
                <span className="text-link" onClick={() => setIsRegister(true)}>
                  Daftar di sini
                </span>
              </p>
            </form>
          )}
        </div>

        <div className="overlay-container">
          <div className="overlay">
            <div className="overlay-panel overlay-right"></div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={modal.isOpen}
        type={modal.type}
        message={modal.message}
        onConfirm={modal.onConfirm}
        onClose={closeModal}
        confirmOnly={modal.confirmOnly} 
      />
    </div>
  );
};

export default AuthPage;